import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Button, Select } from "@/components/common";
import { lgpdService } from "@/services/lgpdService";
import type { SubjectRequestCreated, SubjectRequestType } from "@/types/lgpd";
import { formatDate } from "@/utils/fmt";
import "@/components/auth/LoginPage.css";
import "./SubjectRequestPage.css";

const TYPE_OPTIONS: { value: SubjectRequestType; label: string }[] = [
  { value: "access", label: "Acessar meus dados" },
  { value: "correction", label: "Corrigir meus dados" },
  { value: "portability", label: "Portabilidade dos dados" },
  { value: "anonymization", label: "Anonimizar meus dados" },
  { value: "deletion", label: "Excluir meus dados" },
  { value: "consent_revocation", label: "Revogar consentimento" },
  { value: "sharing_information", label: "Informação sobre compartilhamento" },
];

export function SubjectRequestPage() {
  const [email, setEmail] = useState("");
  const [type, setType] = useState<SubjectRequestType>("access");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubjectRequestCreated | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const created = await lgpdService.submitRequest({
        email: email.trim(),
        type,
        description: description.trim() || undefined,
      });
      setResult(created);
    } catch {
      setError("Não foi possível registrar sua solicitação. Tente novamente em alguns minutos.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={onSubmit}>
        <h1 className="login-title">Solicitação de privacidade</h1>
        {result ? (
          <>
            <p className="login-sub">
              Recebemos sua solicitação. Guarde o protocolo abaixo — usaremos para acompanhar o
              atendimento.
            </p>
            <div className="login-field">
              <span>Protocolo</span>
              <strong style={{ fontSize: "1.25rem", letterSpacing: "0.02em" }}>
                {result.protocol}
              </strong>
            </div>
            <p className="login-sub">
              Prazo de resposta: <strong>{formatDate(result.response_deadline)}</strong> (15 dias
              úteis, conforme a LGPD).
            </p>
            <Link to="/login" className="login-link">
              Voltar ao início
            </Link>
          </>
        ) : (
          <>
            <p className="login-sub">
              Exerça seus direitos como titular de dados (Art. 18 da LGPD). Informe o e-mail
              cadastrado e o tipo de pedido.
            </p>
            <label className="login-field">
              <span>E-mail</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                autoFocus
              />
            </label>
            <label className="login-field">
              <span>Tipo de solicitação</span>
              <Select<SubjectRequestType> value={type} onChange={setType} options={TYPE_OPTIONS} />
            </label>
            <label className="login-field">
              <span>Descrição (opcional)</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Detalhe seu pedido, se necessário."
              />
            </label>
            {error && <p className="login-error">{error}</p>}
            <Button type="submit" variant="primary" disabled={submitting || !email.trim()}>
              {submitting ? "Enviando…" : "Enviar solicitação"}
            </Button>
            <Link to="/login" className="login-link">
              Voltar ao login
            </Link>
          </>
        )}
      </form>
    </div>
  );
}
